/**
 * Text utility functions for character counting and validation.
 * Handles markup for supers fields: bolded text (red styling) and forward slash (line breaks).
 */

/**
 * Extracts visible text from supers value by removing markup characters.
 * - Removes markup markers (used for red text)
 * - Treats forward slash as line break separators, joining lines with a space
 * - Trims whitespace from each line
 *
 * Examples:
 * - "Drawing a <bolded>cute friend</bolded>/ for Snowy!" becomes "Drawing a cute friend for Snowy!"
 * - "아니면 <bolded>가족</bolded> / <bolded>구성원인가</bolded>요?" becomes "아니면 가족 구성원인가요?"
 */
export function getSupersVisibleText(value: string): string {
  if (!value || typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/\*\*/g, '') // Remove red text markers
    .split('/') // Split on line breaks
    .map((part) => part.trim()) // Trim whitespace from each line
    .filter(Boolean) // Remove empty lines
    .join(' '); // Join lines with space
}

/**
 * Counts Unicode characters correctly, including multi-byte characters like Korean.
 * Uses Array.from() to properly handle emoji and other multi-code-point characters.
 *
 * Examples:
 * - "Hello" = 5 chars
 * - "가족" = 2 chars
 * - emoji = 1 char
 */
export function getUnicodeCharCount(value: string): number {
  if (!value || typeof value !== 'string') {
    return 0;
  }

  return Array.from(value).length;
}

/**
 * Gets the visible character count for a text field.
 * For supers fields (text2_supers, text3_supers), extracts visible text first.
 * For other text fields, uses direct length.
 *
 * @param value - The text value
 * @param fieldId - The slot ID to determine field type
 * @returns Unicode character count of visible text
 */
export function getVisibleCharCount(value: string, fieldId: string): number {
  if (!value || typeof value !== 'string') {
    return 0;
  }

  const isSupersField = fieldId === 'text2_supers' || fieldId === 'text3_supers';

  if (isSupersField) {
    const visibleText = getSupersVisibleText(value);
    return getUnicodeCharCount(visibleText);
  }

  return getUnicodeCharCount(value);
}
